import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "cartCount",
    "submitButton",
    "totalCount",
    "totalPrice",
    "totalSummary",
    "items",
    "categoryTabs"
  ]
  static values = {
    initialCart: Object,
    brandId:     Number
  }

  currentPages = {}
  touchStartX   = 0

  connect() {
    this.items = []
    this.totalSummaryData = {}

    // 옵션 선택 모달(#modals, 이 컨트롤러 DOM 범위 밖)에서 담기 완료 시 쏘는 이벤트.
    // 결제에 제출할 상품 목록과 금액도 함께 동기화한다.
    this.boundCartUpdated = (event) => this.updateUI(event.detail)
    window.addEventListener("kiosk:cart-updated", this.boundCartUpdated)

    if (this.hasInitialCartValue) {
      const initial = this.initialCartValue
      this.items = initial.items || []
      this.totalSummaryData = initial.total_summary || {}
      this.updateUI(initial)
    }
  }

  disconnect() {
    window.removeEventListener("kiosk:cart-updated", this.boundCartUpdated)
  }

  // ── 카테고리 탭 좌우 스크롤 ────────────────────────────────

  scrollCategoryLeft() {
    this.categoryTabsTarget.scrollBy({ left: -400, behavior: "smooth" })
  }

  scrollCategoryRight() {
    this.categoryTabsTarget.scrollBy({ left: 400, behavior: "smooth" })
  }

  // ── 상품 페이지네이션 ──────────────────────────────────────

  prevPage(event) {
    const catId  = event.currentTarget.dataset.categoryId
    const current = this.currentPages[catId] || 0
    if (current > 0) this.goToPage(catId, current - 1)
  }

  nextPage(event) {
    const catId  = event.currentTarget.dataset.categoryId
    const pages  = document.querySelectorAll(`[data-page-of="${catId}"]`)
    const current = this.currentPages[catId] || 0
    if (current < pages.length - 1) this.goToPage(catId, current + 1)
  }

  goToPage(catId, pageIndex) {
    const pages = document.querySelectorAll(`[data-page-of="${catId}"]`)
    pages.forEach((p, i) => p.classList.toggle("hidden", i !== pageIndex))
    this.currentPages[catId] = pageIndex

    const info = document.querySelector(`[data-page-info="${catId}"]`)
    if (info) info.textContent = `${pageIndex + 1} / ${pages.length}`

    document.querySelectorAll(`[data-action*="kiosk#prevPage"][data-category-id="${catId}"]`)
      .forEach(btn => { btn.disabled = pageIndex === 0 })
    document.querySelectorAll(`[data-action*="kiosk#nextPage"][data-category-id="${catId}"]`)
      .forEach(btn => { btn.disabled = pageIndex === pages.length - 1 })
  }

  resetPage(event) {
    const catId = event.currentTarget.dataset.categoryId
    this.goToPage(catId, 0)
  }

  // ── 터치 슬라이드 ─────────────────────────────────────────

  touchStart(event) {
    this.touchStartX = event.touches[0].clientX
  }

  touchEnd(event) {
    const deltaX = event.changedTouches[0].clientX - this.touchStartX
    if (Math.abs(deltaX) < 60) return
    const catId  = event.currentTarget.dataset.categoryId
    const pages  = document.querySelectorAll(`[data-page-of="${catId}"]`)
    const current = this.currentPages[catId] || 0
    if (deltaX < 0 && current < pages.length - 1) this.goToPage(catId, current + 1)
    if (deltaX > 0 && current > 0) this.goToPage(catId, current - 1)
  }

  // ── 장바구니 조작 ──────────────────────────────────────────

  async addItem(event) {
    event.preventDefault()
    const posItemId = event.currentTarget.dataset.posItemId
    await this.cartRequest("cart_add_item", { pos_item_id: posItemId })
  }

  async incrementItem(event) {
    event.preventDefault()
    const posItemId = event.currentTarget.dataset.posItemId
    const code = event.currentTarget.dataset.code
    const count = parseInt(event.currentTarget.dataset.currentCount) + 1
    await this.cartRequest("cart_set_count", { pos_item_id: posItemId, count, code })
  }

  async decrementItem(event) {
    event.preventDefault()
    const posItemId = event.currentTarget.dataset.posItemId
    const code = event.currentTarget.dataset.code
    const count = parseInt(event.currentTarget.dataset.currentCount) - 1
    await this.cartRequest("cart_set_count", { pos_item_id: posItemId, count, code })
  }

  async removeItem(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.code
    await this.cartRequest("cart_remove", { code })
  }

  async cartRequest(action, payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`/pos/${this.brandIdValue}/kiosk_${action}`, {
        method:  "POST",
        headers: {
          "X-CSRF-Token": csrf,
          "Content-Type": "application/json",
          "Accept":       "text/vnd.turbo-stream.html, application/json"
        },
        body: JSON.stringify(payload)
      })

      const contentType = response.headers.get("Content-Type") || ""
      if (contentType.includes("application/json")) {
        const data = await response.json()
        if (data.status !== "ok") console.warn("Kiosk cart:", data.message)
        return
      }

      const html = await response.text()
      window.Turbo.renderStreamMessage(html)

      const cartHeader = response.headers.get("X-Kiosk-Cart")
      if (cartHeader) {
        const cartData = JSON.parse(cartHeader)
        this.items = cartData.items || []
        this.totalSummaryData = cartData.total_summary || {}
        this.updateUI(cartData)
      }
    } catch (e) {
      console.error("Kiosk cart request failed:", e)
    }
  }

  updateUI(cartData) {
    this.items = cartData.items || []
    const ts = cartData.total_summary || {}
    this.totalSummaryData = ts
    const count = cartData.cart_count || 0

    if (this.hasCartCountTarget) {
      this.cartCountTarget.textContent = count
    }

    if (this.hasSubmitButtonTarget) {
      const enabled = cartData.submit_button_enabled && count > 0
      this.submitButtonTarget.disabled = !enabled
      this.submitButtonTarget.classList.toggle("disabled", !enabled)
    }

    if (this.hasTotalCountTarget) {
      this.totalCountTarget.value = ts.total_count || 0
    }
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.value = (ts.total_price || 0).toLocaleString("ko-KR")
    }
    if (this.hasTotalSummaryTarget) {
      this.totalSummaryTarget.value = JSON.stringify(ts)
    }
    if (this.hasItemsTarget) {
      this.itemsTarget.value = JSON.stringify(this.items)
    }
  }
}
